import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { ADSLocaleProvider, ADSThemeProvider } from '@qbe/ads-core';
import { ContentProvider } from '@qbe/dfc-content';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';

import ErrorBoundary from '~/components-ds/ErrorBoundary';
import { LoadQBEThemeFonts } from '~/components-ds/Fonts';
import HelperTextProvider from '~/components-ds/HelperTextProvider';
import { SnackbarProvider } from '~/components-ds/Snackbar';
import { LOCAL_STORAGE_EMAIL_KEY } from '~/constants/app';
import { useBreakpoint } from '~/hooks-ds/useBreakpoint';
import { setInitialized, updateGroups, updateSpaId, updateUserEmail } from '~/slices/appDetails';

import ModalsAndFloating from './components/ModalsAndFloating';
import { config } from './config';
import Routes from './Routes';
interface Props {
  spaId?: string;
}

const App: React.FC<Props> = ({ spaId }) => {
  const location = useLocation();
  const { isMobile } = useBreakpoint();
  const dispatch = useDispatch();

  if (!spaId) throw Error('ContentProvider: sitecoreItemId not set');
  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      const offset = isMobile ? 110 : 170;
      if (el) {
        const { top, left } = el?.getBoundingClientRect() ?? {};
        window.scrollTo({
          top: top + window.scrollY - offset,
          left,
          behavior: 'smooth',
        });
      }
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
  }, [dispatch, isMobile, location.hash]);

  useEffect(() => {
    const groups = new URLSearchParams(location.search).getAll('group');

    if (groups) dispatch(updateGroups(groups));
    if (groups.includes('staff')) {
      const email = localStorage.getItem(LOCAL_STORAGE_EMAIL_KEY);
      if (email) dispatch(updateUserEmail(email));
    }
    dispatch(setInitialized());
    dispatch(updateSpaId(spaId));
  }, [dispatch, location.search, spaId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <>
      <LoadQBEThemeFonts />
      <ADSLocaleProvider dateAdapter={AdapterDateFns}>
        <ADSThemeProvider>
          <ErrorBoundary>
            <ContentProvider
              sitecoreApiVersion="v4"
              sitecoreConfigurationApiUrl={`${config.sitecoreConfigurationApi}?id=${spaId}`}
            >
              <SnackbarProvider>
                <HelperTextProvider>
                  <Routes />
                  <ModalsAndFloating />
                </HelperTextProvider>
              </SnackbarProvider>
            </ContentProvider>
          </ErrorBoundary>
        </ADSThemeProvider>
      </ADSLocaleProvider>
    </>
  );
};
export default App;
